import { useCallback, useContext, useMemo, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import MarkdownRender from 'LuxKit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import TraderInformation from './TraderInformation'
import BedbankThingsToKnow from 'components/Common/ThingsToKnow/BedbankThingsToKnow'
import LayoutContainer from 'components/Common/LayoutContainer'
import StaticPropertyMapWithInteractiveModal from 'components/Common/PropertyMap/StaticPropertyMapWithInteractiveModal'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import BodyText from 'LuxKit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Modal from 'LuxKit/Modal/Modal'
import UserReviewSection from './UserReviewSection'
import TextLink from 'LuxKit/TextLink'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Sheet from 'components/utils/Sheet'
import Divider from 'LuxKit/Divider'
import ModalContext from 'contexts/ModalContext'
import useNavigableOfferContentScroll, {
  BEDBANK_NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
} from './useNavigableOfferContentScroll'
import { useNavigableOfferContentFocusTargets } from './useNavigableOfferContentFocusTargets'
import { hasMapCoordinates } from './mapCoordinates'
import OpenInGoogleMapsButton, { LocationAddressRow } from './OpenInGoogleMapsButton'

import { maybeTrackReadMoreClientEvent } from './analytics'
import { OfferPageSnapshotContributionRegistration } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import { buildOfferPageAccordionContribution } from 'tripPlanner/assistant/surfaces/offerPage/offerPageAccordionContribution'

const BEDBANK_LOCATION_MAP_HEIGHT = 316
interface Props {
  hash?: string
  offer: App.BedbankOffer
  traderInformation?: App.TraderInformation
}

const TwoColumns = styled.div`
  ${mediaQueryUp.tablet} {
    columns: 2;

    > * {
      break-inside: avoid;
    }
  }
`

const FacilitiesList = styled.ul`
  list-style-type: none;
  margin: 0;
`

function offerHasCoordinates(offer: App.BedbankOffer): boolean {
  return hasMapCoordinates(offer.property)
}

interface BedbankLocationMapProps {
  id: string
  latitude: number
  longitude: number
  name: string
}

function BedbankLocationMap({
  id,
  latitude,
  longitude,
  name,
}: BedbankLocationMapProps) {
  const markers = useMemo<Array<App.MapMarker>>(() => [{
    id,
    type: PROPERTY_MAP_MARKER_TYPE,
    latitude,
    longitude,
  }], [id, latitude, longitude])

  return (
    <StaticPropertyMapWithInteractiveModal
      height={BEDBANK_LOCATION_MAP_HEIGHT}
      markers={markers}
      modalTitle={name}
    />
  )
}

interface ContentField {
  id: string
  label: string
  open: boolean
  initialHeight?: number
  minimumContentHeight?: number
  collapsable?: boolean
  contentComponent: React.ReactElement
}

interface ContentFieldsOptions {
  intl: ReturnType<typeof useIntl>
}

function getContentFields(props: Props, options: ContentFieldsOptions): Array<ContentField> {
  const {
    hash,
    offer,
  } = props

  const { intl } = options

  const {
    property: {
      location,
      id,
      longitude,
      latitude,
      attractionsOverride,
      facilitiesOverride,
    },
  } = offer

  const contentFields: Array<ContentField> = []

  if (offerHasCoordinates(offer)) {
    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'WR071s0Z7fcx-dZBgzI2',
        defaultMessage: 'Location',
        description: 'Location section label for bedbank navigable offer content',
      }),
      open: hash === '#getting-there',
      initialHeight: 410,
      contentComponent: <VerticalSpacer gap={16}>
        <BedbankLocationMap
          id={id}
          latitude={latitude}
          longitude={longitude}
          name={offer.property.name}
        />
        <div>
          <LocationAddressRow>
            <BodyText variant="medium" weight="bold" data-testid="offer-location">{location}</BodyText>
            <OpenInGoogleMapsButton
              query={location}
            />
          </LocationAddressRow>
        </div>
        {offer.attractions &&
          <div>
            <BodyText variant="medium" weight="bold" data-testid="offer-location">
              {intl.formatMessage({
                id: 'WQqpuwNNM5VGtmGpLN4-',
                defaultMessage: 'What’s nearby',
                description: "What's nearby section heading for bedbank navigable offer content",
              })}
            </BodyText>
            <MarkdownRender fontSize="medium" content={attractionsOverride ?? offer.attractions} />
          </div>}
      </VerticalSpacer>,
    })
  }

  if (offer.facilityGroups?.length || facilitiesOverride) {
    contentFields.push({
      id: 'facilities-all',
      label: intl.formatMessage({
        id: 'ymaaFTh8RtrPQFtQoW0-',
        defaultMessage: 'Facilities',
        description: 'Facilities section label for bedbank navigable offer content',
      }),
      open: hash === '#facilities-all',
      initialHeight: 120,
      contentComponent: <TwoColumns>
        {facilitiesOverride && (
          <div data-testid="offer-location">
            <MarkdownRender fontSize="medium" content={facilitiesOverride} />
          </div>
        )}
        {!facilitiesOverride && offer.facilityGroups.map(facilityGroup => (
          <div key={facilityGroup.name}>
            <BodyText variant="medium" weight="bold" data-testid="offer-location">{facilityGroup.name}</BodyText>
            <FacilitiesList>
              {facilityGroup.values.map(value => (
                <li key={value.name}>{value.name}</li>
              ))}
            </FacilitiesList>
          </div>
        ))}
      </TwoColumns>,
    })
  }

  contentFields.push({
    id: 'fine-print',
    label: intl.formatMessage({
      id: '9WsTECHeqsvJ8UhB4MQQ',
      defaultMessage: 'Things to know',
      description: 'Form field label for things to know section on the offer detail page',
    }),
    open: hash === '#fine-print',
    collapsable: false,
    contentComponent: <BedbankThingsToKnow offer={offer} />,
  })

  if (offer.property?.rating?.reviewsTotal && offer.property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'DWFLDJmGS18T_5q4dx15',
        defaultMessage: 'Reviews',
        description: 'Reviews section label for bedbank navigable offer content',
      }),
      open: hash === '#customer-reviews',
      initialHeight: 200,
      minimumContentHeight: 2000,
      contentComponent: <UserReviewSection property={offer.property} isLPP />,
    })
  }

  return contentFields
}

function BedbankNavigableOfferContent(props: Props) {
  const intl = useIntl()
  const { hash, offer } = props

  const contentFields = useMemo(
    () => getContentFields(props, { intl }),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- destructured props fields used as stable deps
    [hash, offer, intl],
  )
  const defaultOpenField = contentFields.find(field => field.open)
  const [openFields, setOpenFields] = useState<Record<string, boolean>>(defaultOpenField ? { [defaultOpenField.id]: true } : {})
  const [headerSelected, setHeaderSelected] = useState<string | undefined>(defaultOpenField ? defaultOpenField?.id : undefined)
  const { contentGroupRefs, scrollToContentGroup } = useNavigableOfferContentScroll(
    BEDBANK_NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
  )
  const accordionContribution = useMemo(
    () => buildOfferPageAccordionContribution('navigable', headerSelected),
    [headerSelected],
  )

  const onGroupInView = useCallback((id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
    }
  }, [])

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToContentGroup(id)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields, scrollToContentGroup])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    maybeTrackReadMoreClientEvent(id, !openFields[id])
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  // Idempotent expand (no scroll) used as the assistant focus-target prepare step.
  const openSection = useCallback((id: string) => {
    setOpenFields(previous => (previous[id] ? previous : { ...previous, [id]: true }))
  }, [])

  useNavigableOfferContentFocusTargets({
    contentFields,
    contentGroupRefs,
    openSection,
    scrollOffset: BEDBANK_NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
  })

  const showModal = useContext(ModalContext)

  const onModalOpenToggle = useCallback(() => {
    showModal(<Modal
      title={intl.formatMessage({
        id: 'V_wdSIwSmFmKT5OXJDYb',
        defaultMessage: 'About this supplier',
        description: 'Modal title for trader information in bedbank navigable offer content',
      })}
    >
      <TraderInformation traderInformation={props.traderInformation!} />
    </Modal>)
  }, [showModal, props.traderInformation, intl])

  return (
    <>
      <OfferPageSnapshotContributionRegistration contribution={accordionContribution} />
      <NavigationBar
        contentFields={contentFields}
        onClick={onHeaderClick}
        selectedHeader={headerSelected}
      />
      <LayoutContainer variant="large">
        <Sheet padding="20 0" tabletPadding="56 0">
          <VerticalSpacer gap={20}>
            {contentFields.map((contentField, index) => <React.Fragment key={contentField.id}>
              {index !== 0 && <Divider kind="secondary" direction="horizontal" />}
              <NavigableOfferContentGroup
                heading={contentField.label}
                id={contentField.id}
                ref={(node) => { contentGroupRefs.current[contentField.id] = node }}
                onInViewChange={onGroupInView}
                initialHeight={contentField.initialHeight ?? 96}
                minimumContentHeight={contentField.minimumContentHeight ?? 0}
                open={!!openFields[contentField.id]}
                onToggle={onReadMoreToggle}
                collapsable={contentField.collapsable}
              >
                <BodyText variant="medium">
                  {contentField.contentComponent}
                </BodyText>
              </NavigableOfferContentGroup>
            </React.Fragment>)}
            {props.traderInformation && (
              <div id="trader-information">
                <TextLink size="medium" onClick={onModalOpenToggle}>
                  {intl.formatMessage({
                    id: 'V_wdSIwSmFmKT5OXJDYb',
                    defaultMessage: 'About this supplier',
                    description: 'Modal title for trader information in bedbank navigable offer content',
                  })}
                </TextLink>
              </div>
            )}
          </VerticalSpacer>
        </Sheet>
      </LayoutContainer>
    </>
  )
}

export default BedbankNavigableOfferContent
